import { computed, ref, onMounted, onUnmounted } from 'vue'
import { SWP_EMPLOYEE_ID } from '@/constants/swp.js'

// Format number: show 1 decimal only if needed, otherwise whole number
const fmtNum = num => {
  const rounded = Math.round(num * 10) / 10
  return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)
}

// Format date as dd-mm
const fmtDate = s => {
  if (!s) return ''
  const d = new Date(`${s}T00:00:00`)
  if (isNaN(d)) return s
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// Color constants
const DEFAULT_LABEL_COLORS = {
  // Project/Werktijd - blue shades
  Projectmanagement: '#1e40af',
  Intern: '#3b82f6',
  'Interne activiteiten': '#60a5fa',
  'Interne drempelverlagende uren': '#93c5fd',
  'Performance en Expertisetijd': '#2563eb',
  'Interne uren': '#3b82f6',
  Internal: '#3b82f6',
  Declarabel: '#3b82f6',
  Billable: '#3b82f6',
  'Niet declarabel': '#94a3b8',
  'Non-billable': '#94a3b8',

  // Verlof - green shades
  Vakantiedagen: '#34d399',
  Verlof: '#34d399',
  Leave: '#34d399',
  Feestdagenverlof: '#10b981',
  'Tijd voor tijd': '#059669',
  'Buitengewoon verlof': '#047857',
  'Onbetaald verlof': '#065f46',
  Ouderschapsverlof: '#22c55e',
  Partnerverlof: '#16a34a',
  Zwangerschapsverlof: '#15803d',
  'zorgverlof (kortdurend)': '#166534',

  // Verzuim - red shades
  Ziek: '#ef4444',
  Sick: '#ef4444',

  // Other
  Training: '#8b5cf6',
  Reizen: '#06b6d4',
  Sales: '#22c55e',
  Overig: '#a3a3a3'
}

const PALETTE = [
  '#60a5fa',
  '#f59e0b',
  '#10b981',
  '#ef4444',
  '#8b5cf6',
  '#14b8a6',
  '#f97316',
  '#84cc16',
  '#e11d48',
  '#06b6d4',
  '#a78bfa',
  '#34d399'
]

/**
 * Composable for EmployeeHoursBarChart display configuration.
 * Handles dark mode detection, mobile detection, color mapping,
 * and ECharts option building.
 *
 * @param {Object} props - Component props (reactive)
 * @param {Object} chartData - Return value from useBarChartData (pass null for initial setup)
 */
export function useBarChartConfig(props, chartData) {
  // --- Dark mode / mobile detection ---
  const isDarkMode = ref(false)
  const isMobile = ref(false)

  const updateDarkMode = () => {
    isDarkMode.value = document.documentElement.classList.contains('dark')
  }

  const updateMobile = () => {
    isMobile.value = window.innerWidth < 640
  }

  onMounted(() => {
    updateDarkMode()
    updateMobile()
    const observer = new MutationObserver(updateDarkMode)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    window.addEventListener('resize', updateMobile)
    onUnmounted(() => {
      observer.disconnect()
      window.removeEventListener('resize', updateMobile)
    })
  })

  // --- Color mapping ---
  const colorCache = new Map()

  const getColorFor = label => {
    const key = String(label || 'Overig')
    if (colorCache.has(key)) return colorCache.get(key)
    const entries = Object.entries({ ...DEFAULT_LABEL_COLORS, ...props.labelColors })
    const found = entries.find(([k]) => k.toLowerCase() === key.toLowerCase())
    const color = found ? found[1] : PALETTE[colorCache.size % PALETTE.length]
    colorCache.set(key, color)
    return color
  }

  // --- Billability color ---
  const getBillabilityColor = (percentage, dark) => {
    if (percentage === null || percentage === undefined) {
      return dark ? '#6b7280' : '#9ca3af'
    }
    if (percentage >= props.targetBillability) {
      return dark ? '#4ade80' : '#16a34a'
    } else if (percentage >= props.targetBillability * 0.75) {
      return dark ? '#fbbf24' : '#d97706'
    } else {
      return dark ? '#f87171' : '#dc2626'
    }
  }

  const unitSuffix = computed(() => (props.unit === 'days' ? 'd' : 'u'))

  // --- ECharts option (only available when chartData is provided) ---
  const option = computed(() => {
    if (!chartData) return {}

    const {
      weekRanges,
      currentWeekIndex,
      categories,
      billabilityPerWeek,
      capacityValue,
      weeklyCapacity,
      dataSeriesBase,
      submittedSeries,
      potentialAssignmentData,
      getProjectsForWeek
    } = chartData

    const weekCount = weekRanges.value.length
    const dark = isDarkMode.value

    const barWidth = isMobile.value
      ? weekCount > 16
        ? 12
        : weekCount > 10
          ? 18
          : weekCount > 6
            ? 25
            : 40
      : undefined

    const colors = {
      textPrimary: dark ? '#e5e7eb' : '#374151',
      textSecondary: dark ? '#9ca3af' : '#6b7280',
      textMuted: dark ? '#6b7280' : '#9ca3af',
      border: dark ? '#4b5563' : '#e5e7eb',
      tooltipBg: dark ? '#1f2937' : '#ffffff',
      tooltipBorder: dark ? '#374151' : '#e5e7eb',
      projectText: dark ? '#d1d5db' : '#374151'
    }

    // Calculate max for Y-axis
    const maxPerWeek = Array(weekCount).fill(0)
    for (const series of submittedSeries.value) {
      series.data.forEach((val, i) => {
        maxPerWeek[i] += Number(val) || 0
      })
    }
    dataSeriesBase.value.assigned.forEach((val, i) => {
      maxPerWeek[i] += Number(val) || 0
    })
    // Add proposed hours
    dataSeriesBase.value.proposed.forEach((val, i) => {
      maxPerWeek[i] += Number(val) || 0
    })
    potentialAssignmentData.value.forEach((val, i) => {
      maxPerWeek[i] += Number(val) || 0
    })

    const isSWP = props.employee?.simplicate_id === SWP_EMPLOYEE_ID
    const maxData = Math.max(0, ...maxPerWeek)
    const maxWeeklyCapacity = isSWP
      ? 0
      : weeklyCapacity.value.length > 0
        ? Math.max(0, ...weeklyCapacity.value)
        : capacityValue.value
    const yMax = isSWP ? Math.max(40, maxData, 1) : Math.max(maxWeeklyCapacity || 0, maxData, 1)

    // Build series array
    const assignedSum = dataSeriesBase.value.assigned.reduce((a, v) => a + (Number(v) || 0), 0)
    const proposedSum = dataSeriesBase.value.proposed.reduce((a, v) => a + (Number(v) || 0), 0)
    const potentialSum = potentialAssignmentData.value.reduce((a, v) => a + (Number(v) || 0), 0)

    const series = []

    // Submitted series FIRST (bottom)
    series.push(...submittedSeries.value)

    // Gepresourced (middle)
    if (assignedSum > 0) {
      series.push({
        name: 'Gepresourced',
        type: 'bar',
        stack: 'work',
        barMaxWidth: barWidth,
        data: dataSeriesBase.value.assigned,
        itemStyle: { color: '#9333ea' }
      })
    }

    // Voorgesteld (renders after Gepresourced)
    if (proposedSum > 0) {
      series.push({
        name: 'Voorgesteld',
        type: 'bar',
        stack: 'work',
        barMaxWidth: barWidth,
        data: dataSeriesBase.value.proposed,
        itemStyle: { color: '#eab308' }
      })
    }

    // Potential assignment LAST (top)
    if (potentialSum > 0) {
      series.push({
        name: props.potentialLabel,
        type: 'bar',
        stack: 'work',
        barMaxWidth: barWidth,
        data: potentialAssignmentData.value,
        itemStyle: {
          color: dark ? '#6b7280' : '#9ca3af',
          decal: {
            symbol: 'rect',
            symbolSize: 1,
            rotation: -Math.PI / 4,
            dashArrayX: [1, 0],
            dashArrayY: [4, 3],
            color: dark ? '#9ca3af' : '#6b7280'
          }
        }
      })
    }

    // Capacity line
    const maxCapacity = Math.max(0, ...weeklyCapacity.value)
    if (maxCapacity > 0 && !isSWP) {
      series.push({
        name: 'Capaciteit',
        type: 'line',
        data: weeklyCapacity.value,
        silent: true,
        showSymbol: false,
        lineStyle: {
          color: '#dc2626',
          type: 'dashed',
          width: 2
        },
        legendHoverLink: false
      })
    }

    return {
      animation: false,
      aria: {
        enabled: true,
        decal: { show: true }
      },
      grid: {
        left: isMobile.value ? 35 : 50,
        right: isMobile.value ? 10 : 50,
        top: isMobile.value ? 35 : 50,
        bottom: props.showBillability
          ? 55
          : isMobile.value && weekCount > 8
            ? 45
            : isMobile.value
              ? 30
              : 40,
        containLabel: false
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        confine: isMobile.value,
        appendToBody: !isMobile.value,
        backgroundColor: colors.tooltipBg,
        borderColor: colors.tooltipBorder,
        textStyle: { color: colors.textPrimary, fontSize: isMobile.value ? 11 : 12 },
        extraCssText: isMobile.value
          ? 'z-index: 9999; max-width: 280px; white-space: normal;'
          : 'z-index: 9999;',
        position: isMobile.value
          ? undefined
          : (point, params, dom, rect, size) => {
              const chartRect = rect || { x: 0, y: 0 }
              const viewportX = chartRect.x + point[0]
              const viewportY = chartRect.y + point[1]

              const x = viewportX + 15
              const y = Math.max(10, viewportY - size.contentSize[1] / 2)

              const maxX = window.innerWidth - size.contentSize[0] - 20

              return [Math.min(x, maxX), y]
            },
        formatter: params => {
          if (!params || !params.length) return ''

          const weekIndex = params[0].dataIndex
          const weekData = weekRanges.value[weekIndex]
          const weekLabel = weekData?.iso_week || ''
          const dateRange = weekData ? `${fmtDate(weekData.start)} - ${fmtDate(weekData.end)}` : ''

          const lines = [
            `<div style="margin-bottom:6px;"><strong style="color:${colors.textPrimary};">${weekLabel}</strong><br/><span style="font-size:11px;color:${colors.textSecondary};">${dateRange}</span></div>`
          ]

          const submittedItems = params.filter(
            p =>
              p.seriesType === 'bar' &&
              p.seriesName !== 'Gepresourced' &&
              p.seriesName !== 'Voorgesteld' &&
              p.seriesName !== props.potentialLabel &&
              p.seriesName !== 'Capaciteit' &&
              Number(p.value) > 0
          )
          const assignedItem = params.find(
            p => p.seriesName === 'Gepresourced' && Number(p.value) > 0
          )
          const proposedItem = params.find(
            p => p.seriesName === 'Voorgesteld' && Number(p.value) > 0
          )
          const potentialItem = params.find(
            p => p.seriesName === props.potentialLabel && Number(p.value) > 0
          )

          if (submittedItems.length) {
            lines.push(
              `<div style="margin:4px 0;font-size:11px;color:${colors.textSecondary};">Geboekt:</div>`
            )
            for (const p of submittedItems) {
              const val = fmtNum(Number(p.value || 0))
              lines.push(
                `<div style="display:flex;align-items:center;gap:6px;color:${colors.textPrimary};"><span style="display:inline-block;width:10px;height:10px;background:${p.color};border-radius:2px;"></span><span>${p.seriesName}:</span><span style="margin-left:auto;font-weight:500;">${val}${unitSuffix.value}</span></div>`
              )
            }

            const projects = getProjectsForWeek(weekIndex)
            if (projects.length > 0) {
              lines.push(
                `<div style="margin-top:8px;font-size:11px;color:${colors.textSecondary};">Projecten:</div>`
              )
              const maxProjects = 5
              const displayProjects = projects.slice(0, maxProjects)
              for (const proj of displayProjects) {
                const val = fmtNum(Number(proj.hours || 0))
                lines.push(
                  `<div style="display:flex;align-items:center;gap:6px;font-size:11px;"><span style="color:${colors.projectText};">• ${proj.name}</span><span style="margin-left:auto;font-weight:500;color:${colors.textPrimary};">${val}${unitSuffix.value}</span></div>`
                )
              }
              if (projects.length > maxProjects) {
                lines.push(
                  `<div style="font-size:10px;color:${colors.textMuted};font-style:italic;">+ ${projects.length - maxProjects} meer...</div>`
                )
              }
            }
          }

          if (assignedItem) {
            const val = fmtNum(Number(assignedItem.value || 0))
            lines.push(
              `<div style="margin-top:6px;display:flex;align-items:center;gap:6px;color:${colors.textPrimary};"><span style="display:inline-block;width:10px;height:10px;background:${assignedItem.color};border-radius:2px;"></span><span>Gepresourced:</span><span style="margin-left:auto;font-weight:500;">${val}${unitSuffix.value}</span></div>`
            )
          }

          if (proposedItem) {
            const val = fmtNum(Number(proposedItem.value || 0))
            lines.push(
              `<div style="margin-top:6px;display:flex;align-items:center;gap:6px;color:${colors.textPrimary};"><span style="display:inline-block;width:10px;height:10px;background:${proposedItem.color};border-radius:2px;"></span><span>Voorgesteld:</span><span style="margin-left:auto;font-weight:500;">${val}${unitSuffix.value}</span></div>`
            )
          }

          if (potentialItem) {
            const val = fmtNum(Number(potentialItem.value || 0))
            lines.push(
              `<div style="margin-top:6px;display:flex;align-items:center;gap:6px;color:${colors.textPrimary};"><span style="display:inline-block;width:10px;height:10px;background:${potentialItem.color};border-radius:2px;"></span><span>${props.potentialLabel}:</span><span style="margin-left:auto;font-weight:500;">${val}${unitSuffix.value}</span></div>`
            )
          }

          const submittedSum = submittedItems.reduce((a, p) => a + Number(p.value || 0), 0)
          const assignedVal = assignedItem ? Number(assignedItem.value || 0) : 0
          const proposedVal = proposedItem ? Number(proposedItem.value || 0) : 0
          const potentialVal = potentialItem ? Number(potentialItem.value || 0) : 0
          const total = submittedSum + assignedVal + proposedVal + potentialVal

          lines.push(
            `<div style="margin-top:8px;padding-top:6px;border-top:1px solid ${colors.border};font-weight:600;color:${colors.textPrimary};">Totaal: ${fmtNum(total)}${unitSuffix.value}</div>`
          )

          const weekCapacity = weeklyCapacity.value[weekIndex] ?? capacityValue.value
          if (weekCapacity > 0 && !isSWP) {
            const remaining = weekCapacity - total
            const remainingColor = remaining >= 0 ? '#059669' : '#dc2626'
            lines.push(
              `<div style="font-size:11px;color:${remainingColor};">Beschikbaar: ${fmtNum(remaining)}${unitSuffix.value}</div>`
            )
          }

          return lines.join('')
        }
      },
      legend: {
        top: 0,
        type: 'scroll',
        textStyle: { color: colors.textPrimary, fontSize: isMobile.value ? 10 : 12 },
        pageTextStyle: { color: colors.textSecondary },
        pageIconColor: dark ? '#9ca3af' : '#2f4554',
        pageIconInactiveColor: dark ? '#4b5563' : '#aaa',
        itemWidth: isMobile.value ? 14 : 25,
        itemHeight: isMobile.value ? 10 : 14,
        itemGap: isMobile.value ? 6 : 10,
        // Order: Potential (top) -> Voorgesteld -> Gepresourced -> submitted series (bottom)
        data: [
          ...(potentialSum > 0 ? [props.potentialLabel] : []),
          ...(proposedSum > 0 ? ['Voorgesteld'] : []),
          ...(assignedSum > 0 ? ['Gepresourced'] : []),
          ...submittedSeries.value.map(s => s.name)
        ]
      },
      xAxis: {
        type: 'category',
        data: categories.value,
        axisLabel: {
          fontSize: isMobile.value ? 9 : 11,
          interval: isMobile.value
            ? weekCount > 12
              ? 3
              : weekCount > 6
                ? 1
                : 0
            : weekCount > 40
              ? 3
              : weekCount > 20
                ? 1
                : 0,
          rotate: isMobile.value && weekCount > 8 ? 45 : 0,
          formatter: (value, index) => {
            const isCurrentWeek = props.showCurrentWeekHighlight && index === currentWeekIndex.value
            if (props.showBillability && billabilityPerWeek.value.length > index) {
              const pct = billabilityPerWeek.value[index]
              return isCurrentWeek
                ? `{currentWeek|${value}}\n{pct${index}|${pct}%}`
                : `{week|${value}}\n{pct${index}|${pct}%}`
            }
            return isCurrentWeek ? `{currentWeek|${value}}` : `{week|${value}}`
          },
          rich: (() => {
            const richConfig = {
              week: {
                fontSize: isMobile.value ? 9 : 11,
                color: colors.textPrimary,
                lineHeight: 16
              },
              currentWeek: {
                fontSize: isMobile.value ? 9 : 11,
                color: '#86efac',
                fontWeight: 700,
                lineHeight: 16
              }
            }
            if (props.showBillability) {
              billabilityPerWeek.value.forEach((pct, index) => {
                richConfig[`pct${index}`] = {
                  fontSize: 10,
                  color: getBillabilityColor(pct, dark),
                  fontWeight: 600,
                  lineHeight: 14
                }
              })
            }
            return richConfig
          })()
        },
        axisLine: { lineStyle: { color: colors.border } },
        axisTick: { alignWithLabel: true, lineStyle: { color: colors.border } }
      },
      yAxis: {
        type: 'value',
        name: props.unit === 'days' ? 'Dagen' : 'Uren',
        nameTextStyle: { fontSize: isMobile.value ? 9 : 11, color: colors.textSecondary },
        axisLabel: {
          formatter: v => `${v}${unitSuffix.value}`,
          fontSize: isMobile.value ? 9 : 11,
          color: colors.textSecondary
        },
        axisLine: { lineStyle: { color: colors.border } },
        max: Math.ceil(yMax * 1.1),
        splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
      },
      series
    }
  })

  return {
    isDarkMode,
    isMobile,
    getColorFor,
    option
  }
}
